import { useState, useEffect } from 'react';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { ApiError } from '../types';

export function useMcpApi(userId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [mcpServerPluginUrl, setMcpServerPluginUrl] = useState<string>('');
  const { fetch } = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  async function loadMcpServerUrl() {
    try {
      const url = await discoveryApi.getBaseUrl("mcp-server");
      setMcpServerPluginUrl(url);
    } catch (error) {
      console.error('Error loading MCP server URL:', error);
      setError({
        message: 'Error Loading MCP Server URL',
        details: error instanceof Error ? error.message : 'An unknown error occurred'
      });
    }
  }

  useEffect(() => {
    loadMcpServerUrl();
  }, []);

  const sendQuery = async (query: string): Promise<string | null> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!mcpServerPluginUrl) {
        throw new Error('MCP server URL not available');
      }

      let response;
      try {
        response = await fetch(`${mcpServerPluginUrl}/agent/query`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query, userId }),
        });
      } catch (networkError) {
        const errMsg = `Network error in request to MCP server: ${networkError}`;
        console.error(errMsg);

        setError({ message: 'Connection Error', details: errMsg });
        return null;
      }

      if (!response.ok) {
        let errorDetails;
        try {
          const errorData = await response.json();

          if (errorData.error) {
            errorDetails = JSON.stringify(errorData.error);
          } else if (errorData.message) {
            errorDetails = JSON.stringify(errorData.message);
          } else {
            errorDetails = `Status: ${response.status} ${response.statusText}`;
          }
        } catch (e) {
          errorDetails = `Status: ${response.status} ${response.statusText}`;
        }

        setError({
          message: 'Error',
          details: errorDetails
        });
        console.error(`Error in MCP request: ${errorDetails}`);
        return null;
      }

      // Process successful response
      try {
        const data = await response.json();
        if (data.result) {
          console.log('MCP query result:', data.result);
          return data.result;
        } else {
          const errorMessage = 'The server response did not contain any result data.';
          setError({
            message: 'Unexpected Response Format',
            details: errorMessage
          });
          return null;
        }
      } catch (parseError) {
        console.error('Error parsing JSON response:', parseError);
        const errorMessage = 'The server response could not be interpreted correctly.';
        setError({
          message: 'Error Processing Response',
          details: errorMessage
        });
        return null;
      }
    } catch (error) {
      console.error('Unexpected error in MCP request:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      setError({
        message: 'Unexpected Error',
        details: errorMessage
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    setError,
    sendQuery
  };
}
